import { z } from "zod";
import { responseSchema } from "../http.js";
import { applyPercentDiscount, catalogItemSchema, percentPriceMap, priceMapSchema, productStatusSchema, productTranslationsSchema, slugSchema, volumePointsSchema, } from "./product.js";
import { packageShippingSchema } from "./shipping.js";
/**
 * ═══════════════════════════════════════════════════════════
 * Paquetes
 *
 * Un paquete es un conjunto de productos con su propio precio, que se
 * cobra de forma recurrente. Su razón de ser, en palabras del documento
 * de dirección:
 *
 *   "Si vendes de tu tienda productos sueltos ganas del sobreprecio, pero
 *    si vendes un paquete te genera las comisiones del plan de referidos"
 *
 * ── Franquicia y paquete son compras SEPARADAS ─────────────
 * Decisión de Anahí (26-ago). Un paquete nunca incluye la franquicia; se
 * compran aparte. El documento contempla una excepción para mayoristas
 * —que un paquete traiga la franquicia incluida— que queda FUERA por
 * ahora y anotada en `docs/01_MODELO_FRANQUICIA_Y_ONBOARDING.md`.
 * ═══════════════════════════════════════════════════════════
 */
// ── Categorías ───────────────────────────────────────────
/**
 * - `personal`: recompra personal. Descuento hasta el 15%.
 * - `franchisee`: negocio digital, para franquiciatarios. Hasta el 30%.
 *
 * Los de franquiciatario NO aparecen en la sección de paquetes personales
 * (documento, pág. 4). El objetivo declarado de la escala de descuento es
 * que la gente se vuelva franquiciataria: por eso el techo es distinto.
 */
export const PACKAGE_CATEGORIES = ["personal", "franchisee"];
export const packageCategorySchema = z.enum(PACKAGE_CATEGORIES);
export const PACKAGE_CATEGORY_LABELS = {
    personal: "Recompra personal",
    /*
     * "Mayorista" en la etiqueta, con todas sus letras (Anahí, 27-ago):
     * marcar un paquete en esta categoría ES configurarlo como paquete
     * mayorista — es el que aparece en el modal del primer stock y el que
     * NUNCA sale en la sección de paquetes personales.
     */
    franchisee: "Negocio digital (mayorista)",
};
/** Techo de descuento por categoría, en porcentaje. */
export const MAX_DISCOUNT_PERCENT = {
    personal: 15,
    franchisee: 30,
};
/** Suelo: por debajo de esto un paquete no convence a nadie. */
export const MIN_DISCOUNT_PERCENT = 5;
export const discountPercentSchema = z
    .number()
    .int("El descuento debe ser un porcentaje entero")
    .min(MIN_DISCOUNT_PERCENT, `El descuento mínimo es ${MIN_DISCOUNT_PERCENT}%`)
    .max(50, "Un descuento así de alto necesita revisarse a mano");
// ── Contenido ────────────────────────────────────────────
/**
 * Una línea del paquete. Es LA MISMA definición que usa la composición
 * dentro del producto (`catalogItemSchema` en `product.ts`), no una
 * copia: dos gemelas divergen en el primer cambio de techo y entonces el
 * mismo par `{productId, quantity}` admite 99 unidades por un camino y
 * otra cosa por el otro.
 *
 * El nombre se conserva porque es el que ya usan el modelo, el servicio
 * y las pruebas de este módulo.
 */
export const packageItemSchema = catalogItemSchema;
// ── El paquete ───────────────────────────────────────────
export const packageSchema = z.object({
    id: z.string(),
    slug: z.string(),
    translations: productTranslationsSchema,
    category: packageCategorySchema,
    items: z.array(packageItemSchema).min(1),
    /**
     * El descuento que se escribe al crear el paquete. Es EL dato: de aquí
     * sale el precio, no al revés.
     */
    discountPercent: discountPercentSchema,
    /**
     * Precio de lista: la suma de sus productos a precio normal. Calculado
     * por el servidor a partir de `items`.
     */
    listPrices: priceMapSchema,
    /**
     * Lo que se cobra: lista menos el descuento. También calculado.
     *
     * Ni este ni `listPrices` se escriben a mano. Un precio tecleado aparte
     * del porcentaje se desincroniza en cuanto alguien toca uno de los dos,
     * y entonces la tienda promete un ahorro que no existe — nadie se entera
     * hasta que un cliente saca la calculadora.
     */
    prices: priceMapSchema,
    /**
     * VP que otorga. Se calcula sumando los de sus productos y se guarda ya
     * resuelto: el día que un producto cambie de puntos, los paquetes ya
     * vendidos no deben cambiar de valor retroactivamente.
     */
    volumePoints: volumePointsSchema,
    /**
     * La caja real del paquete (Anahí, 21-sep-2026): peso y medidas que
     * viajan a Trasso. Cuando falta un campo, manda la suma de sus
     * productos — ver `shippingOfPackage` en `shipping.ts`.
     */
    shipping: packageShippingSchema.optional(),
    /**
     * ── Precio y puntos FIJOS (Anahí, 14-sep-2026) ─────────────────────
     * Los paquetes del sistema viejo se venden a un precio negociado
     * (KURA $4,000 / 230 USD, HOLOS $8,500 / 510…) que NO sale de ningún
     * porcentaje sobre la lista: la lista − 30 % los dejaba 3–18 % más
     * caros que en producción. Y sus puntos también son fijos (KURA 150,
     * HOLOS 400), no la suma de sus productos.
     *
     * Cuando existen, MANDAN: `prices` y `volumePoints` se copian de aquí
     * en vez de derivarse. Se guardan aparte de los derivados para que la
     * tienda siga sabiendo cuánto se ahorra frente a la lista, y para que
     * al quitarlos el paquete vuelva solo a su cálculo. Por moneda: un
     * paquete puede fijar MXN y dejar USD derivado.
     */
    fixedPrices: priceMapSchema.optional(),
    fixedVolumePoints: volumePointsSchema.optional(),
    /** Descuento EXTRA para franquiciatarios activos, sobre este paquete. */
    franchiseeDiscountPercent: z.number().min(0).max(50).optional(),
    /**
     * ── "Franquicia incluida" (Anahí, 27-ago) ──────────────────────────
     * SOLO para paquetes mayoristas (`category: "franchisee"`): comprarlo
     * adquiere también la Franquicia Digital y se cobra ÚNICAMENTE el
     * paquete. Imposible en paquetes personales — el servicio lo rechaza.
     *
     * (La "reserva de comisión" de 2,700 MXN que esto descontaba se quitó
     * el 14-sep-2026 con el bono directo de franquicia.)
     */
    includesFranchise: z.boolean().optional(),
    imageUrl: z.string().optional(),
    status: productStatusSchema,
    sortOrder: z.number().int().default(0),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
// ── El descuento, y el precio que sale de él ─────────────
/** Si el descuento cae dentro de lo permitido para su categoría. */
export function isDiscountWithinBand(percent, category) {
    return percent >= MIN_DISCOUNT_PERCENT && percent <= MAX_DISCOUNT_PERCENT[category];
}
/**
 * Aplica el descuento a un precio de lista.
 *
 * El cálculo vive en `product.ts` (`applyPercentDiscount`) desde que el
 * producto también deriva precios: el de franquiciatario y el de
 * promoción salen de la misma cuenta. Aquí queda el NOMBRE con el que lo
 * llaman este servicio y sus pruebas, delegando en la única fórmula.
 *
 * Redondea al centavo, y hacia abajo: si el redondeo tiene que equivocarse,
 * que sea a favor de quien compra. Un centavo de más multiplicado por miles
 * de cobros recurrentes es una reclamación; un centavo de menos, no.
 */
export function applyDiscount(listCents, percent) {
    return applyPercentDiscount(listCents, percent);
}
/**
 * El precio de cada moneda, ya con el descuento aplicado.
 *
 * Recorre las llaves REALES del mapa. La versión anterior nombraba MXN y
 * USD a mano sobre un mapa abierto, así que un paquete con precio de
 * lista en euros salía con el "antes" tachado y sin el "ahora". Es el
 * mismo error que `product.ts` documenta y evita en su propio mapa: abrir
 * las llaves y luego enumerarlas a mano deja media función cerrada.
 */
export function discountedPrices(list, percent) {
    return percentPriceMap(list, percent);
}
/**
 * El descuento que de verdad se está dando, en por ciento entero.
 *
 * Con precio FIJO el `discountPercent` escrito deja de describir el
 * precio (KURA: lista 5,910 − 30 % daría 4,137, y se cobra 4,000, que es
 * un 32 %). Lo que se le enseña a la gente es la resta real entre la
 * lista y el precio, en la moneda que se está mirando; sin lista o sin
 * precio en esa moneda, cae al porcentaje escrito.
 */
export function effectiveDiscountPercent(pkg, currency = "MXN", 
/** El precio que se está enseñando, si no es `prices` (p. ej. el de franquiciatario). */
priceCents) {
    const list = pkg.listPrices[currency];
    const price = priceCents ?? pkg.prices[currency];
    if (list === undefined || price === undefined || list <= 0 || price > list)
        return pkg.discountPercent;
    return Math.round(((list - price) / list) * 100);
}
/** Cuánto se ahorra, en centavos, en la moneda pedida. */
export function savedAmount(list, prices, currency) {
    const l = list[currency];
    const p = prices[currency];
    if (l === undefined || p === undefined)
        return null;
    return Math.max(0, l - p);
}
// ── Suscripción ──────────────────────────────────────────
/**
 * El paquete se cobra mes con mes ("se carga como costo recurrente a la
 * cuenta del cliente"). Eso lo convierte en una SUSCRIPCIÓN, no en un
 * pedido: hay que saber cuándo toca el próximo cobro y qué pasa si falla.
 *
 * `paused` existe porque el documento pide poder "pausar" productos del
 * paquete; `past_due` porque un cobro que falla no es lo mismo que una
 * baja, y tratarlos igual da de baja a quien solo cambió de tarjeta.
 */
export const SUBSCRIPTION_STATUSES = ["active", "paused", "past_due", "cancelled"];
export const subscriptionStatusSchema = z.enum(SUBSCRIPTION_STATUSES);
export const packageSubscriptionSchema = z.object({
    id: z.string(),
    userId: z.string(),
    packageId: z.string(),
    packageSlug: z.string(),
    status: subscriptionStatusSchema,
    currency: z.enum(["MXN", "USD"]),
    /** Lo que se cobra cada mes, congelado al contratar. */
    priceCents: z.number().int().min(0),
    volumePoints: volumePointsSchema,
    startedAt: z.iso.datetime(),
    nextChargeAt: z.iso.datetime().nullable(),
    cancelledAt: z.iso.datetime().nullable(),
});
// ── Crear y editar ───────────────────────────────────────
export const packageInputSchema = z.object({
    slug: slugSchema,
    translations: productTranslationsSchema,
    category: packageCategorySchema,
    items: z.array(packageItemSchema).min(1, "Un paquete necesita al menos un producto"),
    /**
     * El descuento. NO se manda el precio: lo calcula el servidor sumando
     * los productos del paquete y restando este porcentaje.
     */
    discountPercent: discountPercentSchema,
    /** Ver la nota en `packageSchema`: cuando existen, mandan sobre lo derivado. */
    fixedPrices: priceMapSchema.optional(),
    fixedVolumePoints: volumePointsSchema.optional(),
    /** La caja real; ver la nota en `packageSchema`. */
    shipping: packageShippingSchema.optional(),
    franchiseeDiscountPercent: z.number().min(0).max(50).optional(),
    /** Ver la nota en `packageSchema`: solo cabe en la categoría mayorista. */
    includesFranchise: z.boolean().optional(),
    /*
     * Acepta una URL completa O una ruta del propio sitio (`/assets/…`),
     * igual que el producto. La versión anterior solo admitía URL absoluta,
     * así que una imagen servida por el propio repositorio se rechazaba
     * aquí y se aceptaba allá — la misma dirección, dos respuestas.
     */
    imageUrl: z
        .union([z.url(), z.string().regex(/^\/[^\s]*$/), z.literal("")])
        .optional(),
    status: productStatusSchema.default("draft"),
    sortOrder: z.number().int().default(0),
});
/**
 * Sin `slug` —cambiarlo rompe enlaces— y sin valores por omisión.
 *
 * Lo fijo admite `null` al editar: JSON no puede mandar `undefined`, y
 * "vuelve al cálculo" tiene que poder decirse desde el formulario.
 */
export const packageUpdateSchema = packageInputSchema
    .omit({ slug: true })
    .partial()
    .extend({
    fixedPrices: priceMapSchema.nullable().optional(),
    fixedVolumePoints: volumePointsSchema.nullable().optional(),
    shipping: packageShippingSchema.nullable().optional(),
});
// ── Listado ──────────────────────────────────────────────
export const packageListQuerySchema = z.object({
    category: packageCategorySchema.optional(),
    status: productStatusSchema.optional(),
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(100).default(24),
});
export const packageListSchema = z.object({
    items: z.array(packageSchema),
    total: z.number().int().min(0),
    page: z.number().int().min(1),
    perPage: z.number().int().min(1),
});
export const packageListResponseSchema = responseSchema(packageListSchema);
export const packageResponseSchema = responseSchema(z.object({ package: packageSchema }));
export const PACKAGE_ROUTES = {
    publicList: "/api/packages",
    publicBySlug: (slug) => `/api/packages/${slug}`,
    adminList: "/api/admin/packages",
    adminCreate: "/api/admin/packages",
    adminOne: (id) => `/api/admin/packages/${id}`,
};
//# sourceMappingURL=package.js.map